/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { getMonitorAgentHardwareStats, MonitorAgent } from "@/api/monitor";
import { useTabNavigationContext } from "@/context/tabNavigation";
import { requestFocus } from "@/utils/sidebarFocus";
import { cn } from "@/lib/utils";
import { AnimatedNumber } from "@/components/common/AnimatedNumber";
import { motion } from "motion/react";

type Metric = "cpu" | "memory" | "disk";

const METRICS: { id: Metric; label: string }[] = [
  { id: "cpu", label: "CPU" },
  { id: "memory", label: "Memory" },
  { id: "disk", label: "Disk" },
];

const LIMIT = 8;

/** The busiest servers across the whole fleet, by CPU, memory or fullest disk. */
export const FleetTopWidget: React.FC<{ agents: MonitorAgent[] }> = ({ agents }) => {
  const [metric, setMetric] = useState<Metric>("cpu");
  const { onTabChange } = useTabNavigationContext();
  const active = agents.filter((a) => a.enabled);

  const results = useQueries({
    queries: active.map((a) => ({
      queryKey: ["fleet-hw", a.id],
      queryFn: () => getMonitorAgentHardwareStats(a.id),
      refetchInterval: 30_000,
      staleTime: 15_000,
      retry: false,
    })),
  });

  const rows = active
    .map((a, i) => {
      const hw = results[i]?.data;
      if (!hw) return null;
      const value =
        metric === "cpu"
          ? hw.cpu?.usage_percent
          : metric === "memory"
            ? hw.memory?.used_percent
            : Math.max(0, ...(hw.disks ?? []).map((d) => d.used_percent));
      return value === undefined ? null : { agent: a, value };
    })
    .filter((r): r is { agent: MonitorAgent; value: number } => r !== null)
    .sort((a, b) => b.value - a.value)
    .slice(0, LIMIT);

  const reporting = results.filter((r) => r.data).length;

  return (
    <section className="gh-w">
      <div className="gh-w-head">
        <span className="gh-label">Busiest servers</span>
        <div className="cc-seg" role="tablist" aria-label="Metric">
          {METRICS.map((m) => (
            <button
              key={m.id}
              type="button"
              role="tab"
              aria-selected={metric === m.id}
              className={cn("cc-seg-btn", metric === m.id && "cc-seg-btn--on")}
              onClick={() => setMetric(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="gh-empty">{active.length === 0 ? "no servers yet" : "waiting for data…"}</div>
      ) : (
        <div className="cc-top">
          {rows.map(({ agent, value }) => (
            <motion.button
              layout="position"
              transition={{ duration: 0.28, ease: [0.23, 1, 0.32, 1] }}
              key={agent.id}
              type="button"
              className="cc-top-row"
              title={`Open ${agent.name}`}
              onClick={() => {
                onTabChange("monitor");
                requestFocus("monitor", String(agent.id));
              }}
            >
              <span className="cc-top-name">{agent.name}</span>
              <span className="gh-bar">
                <i className={value >= 90 ? "gh-bar--crit" : value >= 75 ? "gh-bar--warn" : ""} style={{ width: `${Math.min(100, value)}%` }} />
              </span>
              <span className={cn("cc-top-val", value >= 90 && "gh-crit", value >= 75 && value < 90 && "gh-warn")}><AnimatedNumber value={value} mountFrom={null} format={(n) => `${n.toFixed(0)}%`} /></span>
            </motion.button>
          ))}
        </div>
      )}
      <div className="gh-sub cc-top-foot">{`${reporting}/${active.length} servers reporting`}</div>
    </section>
  );
};
